import React, { useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { useTheme, BG_OPTIONS } from "../../ThemeContext";
import { Palette, Check, Save } from "lucide-react";

export default function ThemeTab() {
  const { theme, setTheme, refresh } = useTheme();
  const [draft, setDraft] = useState(theme);
  const [saving, setSaving] = useState(false);

  // Keep draft in sync when theme changes externally
  React.useEffect(() => { setDraft(theme); }, [theme]);

  const preview = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    setTheme(next); // apply immediately for live preview
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/theme", draft);
      toast.success("Tema salvo — aplicado em todo o site");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 card-arya p-6">
        <div className="flex items-center gap-2 mb-4">
          <Palette size={16} className="text-[color:var(--sun-600)]"/>
          <h3 className="font-serif text-2xl">Aparência do site</h3>
        </div>
        <p className="text-sm text-[color:var(--ink-500)] mb-5">
          Escolha o girassol que representa o Arya Studio. As mudanças são aplicadas em tempo real no site inteiro.
        </p>

        <p className="text-[10px] tracking-widest font-display text-[color:var(--sun-700)] mb-2">IMAGEM DE FUNDO</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {BG_OPTIONS.map(opt => {
            const selected = draft.bg_style === opt.id;
            return (
              <button key={opt.id} onClick={()=>preview({ bg_style: opt.id })}
                      aria-pressed={selected}
                      data-testid={`bg-${opt.id}`}
                      className={`text-left rounded-2xl border-2 overflow-hidden transition ${selected ? 'border-[color:var(--sun-600)] shadow-[0_10px_30px_-15px_rgba(199,154,58,.55)]' : 'border-[var(--line)] hover:border-[color:var(--sun-500)]'}`}>
                <div className="aspect-video bg-[var(--cream-100)] relative overflow-hidden">
                  {opt.thumb ? (
                    <img src={opt.thumb} alt={opt.label} className="w-full h-full object-cover"/>
                  ) : opt.id === "sunflower-illustration" ? (
                    <div className="w-full h-full grid place-items-center bg-[radial-gradient(circle,rgba(233,204,133,.4),transparent_70%)]">
                      <svg viewBox="0 0 200 200" width="70" height="70" className="text-[color:var(--sun-600)]">
                        {Array.from({length:20}).map((_,i)=>(
                          <g key={i} transform={`rotate(${i*18} 100 100)`}>
                            <path d="M100 22 C 108 46, 108 62, 100 78 C 92 62, 92 46, 100 22 Z" fill="#E9CC85" stroke="#C79A3A" strokeWidth="0.6"/>
                          </g>
                        ))}
                        <circle cx="100" cy="100" r="22" fill="#7B5615"/>
                      </svg>
                    </div>
                  ) : (
                    <div className="w-full h-full grid place-items-center text-[color:var(--ink-500)] text-xs">Sem imagem</div>
                  )}
                  {selected && (
                    <div className="absolute top-2 right-2 bg-[color:var(--sun-600)] text-white rounded-full p-1"><Check size={12}/></div>
                  )}
                </div>
                <div className="p-3">
                  <p className="font-display font-semibold text-sm">{opt.label}</p>
                  <p className="text-[11px] text-[color:var(--ink-500)] mt-0.5">{opt.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-8">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] tracking-widest font-display text-[color:var(--sun-700)]">NITIDEZ / OPACIDADE</p>
            <span className="font-serif text-2xl text-[color:var(--sun-700)]" data-testid="opacity-value">{draft.bg_opacity}%</span>
          </div>
          <input type="range" min="0" max="80" step="1"
                 value={draft.bg_opacity}
                 onChange={e=>preview({ bg_opacity: Number(e.target.value) })}
                 className="w-full accent-[color:var(--sun-600)]"
                 data-testid="opacity-slider"/>
          <div className="flex justify-between text-[10px] text-[color:var(--ink-500)] mt-1">
            <span>Muito discreto</span><span>Bem visível</span>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-[10px] tracking-widest font-display text-[color:var(--sun-700)] mb-2">POSIÇÃO</p>
          <div className="flex gap-2">
            {[
              { id:"right", label:"Direita" },
              { id:"left", label:"Esquerda" },
              { id:"full", label:"Fundo inteiro" },
            ].map(p => (
              <button key={p.id} onClick={()=>preview({ bg_position: p.id })}
                      aria-pressed={draft.bg_position === p.id}
                      data-testid={`pos-${p.id}`}
                      className={`rounded-full px-4 py-2 text-sm border ${draft.bg_position===p.id ? 'border-[color:var(--sun-600)] bg-[rgba(233,204,133,.15)] text-[color:var(--sun-700)]' : 'border-[var(--line)]'}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2" data-testid="btn-save-theme">
            <Save size={14}/> {saving ? "Salvando..." : "Salvar aparência"}
          </button>
          <button onClick={()=>preview({ bg_style:"sunflower-real", bg_opacity:18, bg_position:"right" })} className="btn-ghost">Restaurar padrão</button>
        </div>
      </div>

      <div className="card-arya p-6 h-fit">
        <h4 className="font-serif text-lg">Dicas</h4>
        <ul className="mt-3 space-y-2 text-sm text-[color:var(--ink-500)] list-disc pl-5">
          <li>Use <b>10–25%</b> para um toque delicado sem competir com o conteúdo.</li>
          <li>Use <b>Fundo inteiro</b> apenas com opacidade abaixo de 15% para não cansar a leitura.</li>
          <li>Você pode trocar quando quiser — todo o site atualiza automaticamente.</li>
        </ul>
      </div>
    </div>
  );
}
